import { PlusOutlined } from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Empty,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Select,
  Spin,
  Table
} from 'antd'
import { FormListFieldData, TableColumnsType } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useEffect, useState } from 'react'

import {
  useCounterpartServiceGetContractEndpoint,
  useStocksServiceGetConsumableEndpointKey
} from 'src/api/queries'
import {
  CreateInventoryEntryItemDto,
  GetConsumableListResponseItem,
  InventoryDocumentResponse2,
  StocksService
} from 'src/api/requests'
import {
  EditableFormItem,
  requiredRules,
  SubmitButtons,
  Text
} from 'src/components'
import { fixedDecimal, getTvaRate } from 'src/features/stock/utils'
import { useDebounce } from 'src/hooks'
import { dayMonthYearBackSlash, disabledPastDate } from 'src/libs'
import { useLocales } from 'src/locales'

import { ProductQuantity } from '../../types'

import { InputConsumablesModal } from './InputConsumablesModal'
import { useStyles } from './styles'

type Props = {
  item?: InventoryDocumentResponse2
  fields: FormListFieldData[]
  form?: FormInstance
  add: () => void
  remove: (index: number) => void
}

type Medicine = {
  tvaUnitRateName?: string
} & CreateInventoryEntryItemDto

const fieldsToReset = [
  'productId',
  'barCode',
  'name',
  'producerName',
  'expirationDate',
  'quantity',
  'unitPrice',
  'priceTotal',
  'group',
  'dose',
  'form',
  'measurementUnitId',
  'measurementUnitName',
  'tvaUnitRateName',
  'tvaUnitRateId',
  'productionDate',
  'tvaTotal'
]

const generateFields = (consumables: { name?: string }[], index?: number) => {
  const targets =
    typeof index === 'number' ? [index] : consumables.map((_, i) => i)

  return targets
    .map((i) => fieldsToReset.map((field) => ['consumables', i, field]))
    .flat()
}

export const PharmaceuticsTable = ({
  fields,
  item,
  form,
  add,
  remove
}: Props) => {
  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)
  const [search, setSearch] = useState('')
  const [productIndex, setProductIndex] = useState<number>()
  const [contractProductsQuantity, setContractProductsQuantity] = useState<
    Map<string, number>
  >(new Map())
  const [contractUnitPrice, setContractUnitPrice] = useState<
    Map<string, number>
  >(new Map())

  const palette = useTheme()

  const debouncedSearch = useDebounce(search)
  const { styles } = useStyles()
  const { t, currentLang } = useLocales()

  const contractId = Form.useWatch('contractId', form)

  const { data: contractConsumables } =
    useCounterpartServiceGetContractEndpoint(
      {
        id: contractId
      },
      [contractId],
      {
        enabled: !!contractId
      }
    )

  const contractProductIds: string[] =
    contractConsumables?.pharmaceutics
      ?.filter((product) => (product?.currentQuantity ?? 0) > 0)
      ?.map((product) => product?.stockProduct?.id)
      .filter((id): id is string => typeof id === 'string') || []

  const {
    data: consumableData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetConsumableEndpointKey,
      debouncedSearch,
      contractId,
      contractProductIds
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getConsumableListEndpoint(
        pageParam,
        100,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        contractProductIds
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetConsumableListResponseItem[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!contractId
  })

  const consumables = consumableData?.pages.flatMap((page) => page.data) || []

  const options = consumables
    .filter((item) => {
      const products = form?.getFieldValue('consumables')

      if (!products) return item

      const currentQuantity = contractProductsQuantity.get(String(item?.id))

      const totalQuantity = products.reduce(
        (acc: number, product: ProductQuantity, index: number) => {
          if (product?.productId === item?.id && index !== editingIndex) {
            acc += Number(product.quantity)
          }

          return acc
        },
        0
      )

      if (Number(currentQuantity) - totalQuantity) {
        return item
      }
    })
    .map((item) => ({
      value: item?.id,
      label: `${item?.commercialName}`,
      item
    }))

  const addConsumable = async () => {
    const consumables = form?.getFieldValue('consumables')

    if (consumables?.length > 0) {
      const fieldsToValidate = generateFields(consumables)

      await form?.validateFields(fieldsToValidate)
    }

    add()
    setEditingIndex(fields.length)
    setIsNewConsumable(true)
  }

  const onSelect = (index: number, value: string) => {
    const data = form?.getFieldValue('consumables')

    const selectedItem = options?.find((item) => item?.value === value)

    if (selectedItem) {
      const selectedProduct = contractConsumables?.pharmaceutics?.find(
        (product) => product?.stockProduct?.id === selectedItem?.item?.id
      )

      const rate = selectedProduct?.unitPrice
        ? +(
            (((selectedProduct?.tvaTotal ?? 0) - selectedProduct.unitPrice) /
              selectedProduct.unitPrice) *
            100
          ).toFixed(2)
        : 0

      data[index] = {
        ...data[index],
        productId: selectedItem?.value,
        name: `${selectedItem?.item?.commercialName}`,
        producerName: selectedItem?.item?.producer?.name,
        group: selectedItem?.item?.group?.name?.[currentLang?.value],
        internationalId: selectedItem?.item?.internationalName?.id,
        measurementUnitName:
          selectedProduct?.measurementUnit?.name?.[currentLang.value],
        measurementUnitId: selectedProduct?.measurementUnit?.id,
        form: {
          id: selectedItem?.item?.form?.id,
          name: selectedItem?.item?.form?.name
        },
        unitPrice: selectedProduct?.unitPrice,
        tvaTotal: selectedProduct?.tvaTotal,
        tvaUnitRateName:
          selectedProduct?.tvaUnitRate?.name?.[currentLang?.value],
        tvaUnitRateId: selectedProduct?.tvaUnitRate?.id,
        tvaUnitRate: rate,
        delivered_quantity:
          (selectedProduct?.quantity ?? 0) -
          (selectedProduct?.currentQuantity ?? 0),
        outstanding_quantity: selectedProduct?.currentQuantity ?? 0
      }

      setSearch('')
      form?.setFieldValue('consumables', data)
    }
  }

  const onSave = async () => {
    const consumables = form?.getFieldValue('consumables')

    if (typeof editingIndex !== 'number') return

    const fieldsToValidate = generateFields(consumables, editingIndex)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      const updatedRow = values.consumables?.[editingIndex]

      const updatedConsumables = [...consumables]
      updatedConsumables[editingIndex] = {
        ...updatedConsumables[editingIndex],
        ...updatedRow
      }

      form?.setFieldsValue({ consumables: updatedConsumables })
      setIsNewConsumable(false)
      setEditingIndex(undefined)
    }
  }

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(fieldsToReset.map((field) => ['stock', index, field]))
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onDelete = (index: number) => {
    remove(index)
  }

  const onDetails = (index: number) => {
    setProductIndex(index)
  }

  const updateTotalPrice = () => {
    const consumables = [...(form?.getFieldValue('consumables') || [])]

    const updatedConsumables = consumables.map((med: Medicine) => {
      const tvaRate = getTvaRate(med?.tvaUnitRateName ?? '')

      return {
        ...med,
        tvaUnitRateName: med.tvaUnitRateName,
        tvaTotal:
          med?.unitPrice && tvaRate !== undefined
            ? Math.round(med?.unitPrice * (1 + tvaRate) * 10000) / 10000
            : undefined,
        priceTotal:
          med?.quantity && med?.unitPrice
            ? Math.round(med.quantity * med.unitPrice * (1 + tvaRate) * 10000) /
              10000
            : undefined
      }
    })

    form?.setFieldsValue({ consumables: updatedConsumables })
  }

  const columns: TableColumnsType = [
    {
      key: 'name',
      width: '20%',
      title: t('common.commercial_naming'),
      render: (_, __, index) => {
        return item ? (
          <Text weight={400} color={palette.blue6}>
            {form?.getFieldValue(['consumables', index, 'name'])}
          </Text>
        ) : (
          <EditableFormItem
            name={[index, 'name']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <Select
              allowClear
              showSearch
              popupMatchSelectWidth={false}
              filterOption={false}
              onSearch={(value) => setSearch(value)}
              loading={isFetchingMedicines}
              options={options}
              onSelect={(value) => onSelect(index, value)}
              placeholder={t('common.active_component')}
              onPopupScroll={(e) => {
                const target = e.target as HTMLElement
                if (
                  target.scrollTop + target.offsetHeight ===
                  target.scrollHeight
                )
                  hasNextMedicinesPage && fetchNextMedicinesPage()
              }}
              notFoundContent={
                isFetchingMedicines ? (
                  <Flex justify="center">
                    <Spin spinning />
                  </Flex>
                ) : (
                  <Empty />
                )
              }
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'barCode',
      width: '10%',
      title: t('stock_applications.series'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'barCode']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <Input placeholder={t('stock_applications.series')} />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'group',
      width: '15%',
      title: t('common.group'),
      render: (_, __, index) => {
        return (
          <Text>{form?.getFieldValue(['consumables', index, 'group'])}</Text>
        )
      }
    },
    {
      key: 'productionDate',
      width: '10%',
      align: 'center',
      title: t('common.production_date'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'productionDate']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <DatePicker
              format={dayMonthYearBackSlash}
              placeholder={t('common.production_date')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'expirationDate',
      width: '10%',
      align: 'center',
      title: t('common.expiration_date'),
      render: (_, __, index) => {
        return (
          <EditableFormItem
            name={[index, 'expirationDate']}
            isEditing={editingIndex === index}
            rules={requiredRules}
          >
            <DatePicker
              format={dayMonthYearBackSlash}
              disabledDate={disabledPastDate}
              placeholder={t('common.expiration_date')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'quantity',
      align: 'center',
      width: '10%',
      title: t('common.quantity'),
      render: (_, __, index) => {
        const outstandingQuantity = form?.getFieldValue([
          'consumables',
          index,
          'outstanding_quantity'
        ])

        return (
          <EditableFormItem
            name={[index, 'quantity']}
            isEditing={editingIndex === index}
            rules={requiredRules?.concat([
              {
                max: outstandingQuantity,
                min: 0,
                message: t('errors.quantity', {
                  max: outstandingQuantity
                }),
                type: 'number'
              }
            ])}
          >
            <InputNumber
              placeholder={t('common.quantity')}
              className="full-width"
              onChange={(value) => {
                const consumables = form?.getFieldValue('consumables') || []

                const updatedConsumables = consumables.map(
                  (med: Medicine, i: number) => {
                    if (i !== editingIndex) return med

                    return {
                      ...med,
                      quantity: value
                    }
                  }
                )

                form?.setFieldsValue({ consumables: updatedConsumables })

                updateTotalPrice()
              }}
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'delivered_quantity',
      width: '10%',
      align: 'center' as const,
      title: t('counterparts.delivered_quantity'),
      render: (_: unknown, __: unknown, index: number) => {
        return (
          <EditableFormItem
            name={[index, 'delivered_quantity']}
            isEditing={false}
          >
            <InputNumber
              min={0}
              placeholder={t('common.delivered_quantity')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'outstanding_quantity',
      width: '10%',
      align: 'center' as const,
      title: t('counterparts.outstanding_quantity'),
      render: (_: unknown, __: unknown, index: number) => {
        return (
          <EditableFormItem
            name={[index, 'outstanding_quantity']}
            isEditing={false}
          >
            <InputNumber
              min={0}
              placeholder={t('common.outstanding_quantity')}
              className="full-width"
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'unitPrice',
      width: '10%',
      align: 'center',
      title: t('stock_administration.price_unit'),
      render: (_, __, index) => {
        const productId = form?.getFieldValue([
          'consumables',
          index,
          'productId'
        ])

        const productUnitPrice = contractUnitPrice.get(productId)

        return (
          <EditableFormItem
            name={[index, 'unitPrice']}
            isEditing={editingIndex === index}
            rules={
              contractId
                ? requiredRules?.concat({
                    max: productUnitPrice,
                    min: 0,
                    message: t('errors.unitPrice', {
                      max: productUnitPrice
                    }),
                    type: 'number'
                  })
                : undefined
            }
          >
            <InputNumber<number>
              placeholder={t('common.quantity')}
              className="full-width"
              onChange={(value) => {
                const consumables = form?.getFieldValue('consumables') || []

                const updatedConsumables = consumables.map(
                  (med: Medicine, i: number) => {
                    if (i !== editingIndex) return med

                    const unitPriceFixed = fixedDecimal(value ?? 0, 4)

                    return {
                      ...med,
                      unitPrice: unitPriceFixed
                    }
                  }
                )

                form?.setFieldsValue({ consumables: updatedConsumables })

                updateTotalPrice()
              }}
            />
          </EditableFormItem>
        )
      }
    },
    {
      key: 'tvaUnitRateName',
      width: '10%',
      align: 'center',
      title: t('common.tva'),
      render: (_, __, index) => {
        const tvaUnitRateName = form?.getFieldValue([
          'consumables',
          index,
          'tvaUnitRateName'
        ])

        return tvaUnitRateName ?? '-'
      }
    },
    {
      key: 'priceTotal',
      width: '10%',
      align: 'center',
      title: t('stock_administration.price_total'),
      render: (_, __, index) => {
        const total = form?.getFieldValue(['consumables', index, 'priceTotal'])

        return total !== undefined ? total : '-'
      }
    },
    ...(!item
      ? [
          {
            width: '10%',
            key: 'actions',
            fixed: 'right' as const,
            render: (_: unknown, __: unknown, index: number) => (
              <SubmitButtons
                editingIndex={editingIndex}
                isEditing={editingIndex === index}
                onSave={onSave}
                onCancel={() => onCancel(index)}
                onEdit={() => setEditingIndex(index)}
                onDelete={() => onDelete(index)}
                onDetails={() => onDetails(index)}
              />
            )
          }
        ]
      : [])
  ]

  useEffect(() => {
    if (contractConsumables?.pharmaceutics?.length && consumables?.length) {
      const filled = contractConsumables?.pharmaceutics
        .filter((p) => !!p?.stockProduct?.id)
        .filter((p) => (p?.currentQuantity ?? 0) > 0)
        .map((product) => {
          const stockProduct = product?.stockProduct
          const tvaRate =
            product?.unitPrice && product?.tvaTotal
              ? +(
                  (((product?.tvaTotal ?? 0) - product?.unitPrice) /
                    product?.unitPrice) *
                  100
                ).toFixed(4)
              : 0

          const selectedItem = options?.find(
            (item) => item?.value === product?.stockProduct?.id
          )

          setContractProductsQuantity((prev) =>
            prev.set(String(stockProduct?.id), Number(product.quantity))
          )

          setContractUnitPrice((prev) =>
            prev.set(String(stockProduct?.id), Number(product.unitPrice))
          )

          return {
            productId: stockProduct?.id,
            name: `${selectedItem?.item?.commercialName}`,
            producerName: selectedItem?.item?.producer?.name,
            group: selectedItem?.item?.group?.name?.[currentLang?.value],
            measurementUnitName:
              product.measurementUnit?.name?.[currentLang.value],
            measurementUnitId: product.measurementUnit?.id,
            internationalId: selectedItem?.item?.internationalName?.id,
            delivered_quantity:
              (product?.quantity ?? 0) - (product?.currentQuantity ?? 0),
            outstanding_quantity: product?.currentQuantity ?? 0,
            form: {
              id: selectedItem?.item?.form?.id,
              name: selectedItem?.item?.form?.name
            },
            unitPrice: product.unitPrice,
            tvaTotal: product.tvaTotal,
            tvaUnitRateName:
              product?.tvaUnitRate?.name?.[currentLang?.value] ?? '',
            tvaUnitRateId: product?.tvaUnitRate?.id,
            tvaUnitRate: tvaRate,
            quantity: undefined
          }
        })

      form?.setFieldsValue({ consumables: filled })
    }
  }, [contractConsumables, currentLang?.value, consumables?.length])

  return (
    <>
      <Text size="xxl">{t('common.pharmaceutics')}</Text>
      <Table
        className={styles.table}
        id="scrollableTable"
        dataSource={fields}
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={columns}
        footer={() =>
          !item && (
            <Flex justify="center">
              <Button
                onClick={addConsumable}
                icon={<PlusOutlined />}
                disabled={!contractId}
              >
                {t('analysis.add_test.add_consumable')}
              </Button>
            </Flex>
          )
        }
      />

      {productIndex !== undefined && (
        <InputConsumablesModal
          form={form}
          productIndex={productIndex}
          onCancel={() => setProductIndex(undefined)}
        />
      )}
    </>
  )
}
